import { CART_CREATE, CART_QUERY, CART_LINES_ADD, CART_LINES_UPDATE, CART_LINES_REMOVE, CART_BUYER_IDENTITY_UPDATE } from '$lib/graphql/mutations/cart';
import type {
  CartCreateMutation, CartCreateMutationVariables,
  CartQuery, CartQueryVariables,
  CartLinesAddMutation, CartLinesAddMutationVariables,
  CartLinesUpdateMutation, CartLinesUpdateMutationVariables,
  CartLinesRemoveMutation, CartLinesRemoveMutationVariables,
  CartBuyerIdentityUpdateMutation, CartBuyerIdentityUpdateMutationVariables
} from '$lib/types/storefront.generated';
import { shClient } from '$lib/shopify.svelte';
import { extractErrorMessage } from '$lib/types/shopify';

import type { CartFieldsFragment } from '$lib/types/storefront.generated';

interface CartContainer {
  cart: CartFieldsFragment | null;
  cartLoading?: boolean;
} 

export let cartContainer = $state<CartContainer>({
  cart: null,
  cartLoading: false
});

export function checkoutUrl() {
  if (cartContainer.cart?.checkoutUrl) {
    return cartContainer.cart.checkoutUrl;
  }
  return null;
}

export function toCheckout() {
  let url = checkoutUrl();
    if (url) {
      window.location.href = url
    }
}

export async function loadSavedCart() {
  const id = localStorage.getItem('sf_cart_id');
  if (!id) return;
  console.log('Loading saved cart with ID:', id);
  cartContainer.cartLoading = true;
  try {
    const cart = await loadCart(id);
    if (cart) {
      cartContainer.cart = cart;
      console.log('Loaded saved cart with this many lines:', cart.lines.edges.length);
    } else {
      // console.warn('No saved cart found, creating a new one');
      // await createCart();
    }
  } catch (error) {
    console.error('Failed to load saved cart:', error);
    await createCart();
  } finally {
    cartContainer.cartLoading = false;
  }
}

const STORAGE_KEY = 'sf_cart_id';

async function loadCart(id: string) {
  const { data } = await shClient.request<CartQuery>(CART_QUERY, { variables: { id } as CartQueryVariables });
  return data?.cart ?? null;
}

async function createCart() {
  // --- create a brand new cart ---
    const res = await shClient.request<CartCreateMutation>(CART_CREATE, {
      variables: { input: {} } satisfies CartCreateMutationVariables
    });

    // network / GraphQL transport errors
    if (res.errors) {
      console.error('cartCreate transport errors', res.errors);
      throw new Error(res.errors?.graphQLErrors?.map(e => e.message).join(', '));
    }

    // Shopify payload userErrors
    const ue = res.data?.cartCreate?.userErrors ?? [];
    if (ue.length) {
      console.error('cartCreate userErrors', ue);
      throw new Error(ue.map(e => `${e.field?.join('.') ?? ''}: ${e.message}`).join(' | '));
    }

    const newCart = res.data?.cartCreate?.cart;
    if (!newCart) {
      console.error('cartCreate response with no cart', res);
      throw new Error('cartCreate returned null cart');
    }
    return newCart;
}

export async function ensureCartId(): Promise<string> {
  cartContainer.cartLoading = true;
  let id = localStorage.getItem(STORAGE_KEY);
  if (id) {
    const existing = await loadCart(id);
    if (existing) {
      cartContainer.cart = existing;
      cartContainer.cartLoading = false;
      return id;
    }
  }
  const fresh = await createCart();
  if (!fresh) throw new Error('Failed to create cart');
  localStorage.setItem(STORAGE_KEY, fresh.id);
  cartContainer.cart = fresh;
  cartContainer.cartLoading = false;
  return fresh.id;
}

export async function addToCart(variantId: string, quantity = 1) {
  const cartId = await ensureCartId();
  const { data, errors } = await shClient.request<CartLinesAddMutation>(CART_LINES_ADD, {
    variables: {
      cartId,
      lines: [{ merchandiseId: variantId, quantity }]
    } satisfies CartLinesAddMutationVariables
  });

  // GraphQL transport errors
  if (errors) {
    console.error('GQL transport errors', errors);
    cartContainer.cartLoading = false;
    throw new Error(extractErrorMessage(errors));
  }

  // userErrors from Shopify
  const userErrors = data?.cartLinesAdd?.userErrors ?? [];
  if (userErrors.length) {
    console.error('Shopify userErrors', userErrors);
    cartContainer.cartLoading = false;
    throw new Error(userErrors.map(e => e.message).join(', '));
  }

  // success
  cartContainer.cart = data!.cartLinesAdd!.cart!; // same fragment everywhere
  cartContainer.cartLoading = false;

  // optional: handle warnings (non-fatal)
  const warnings = (data as any)?.cartLinesAdd?.warnings;
  if (warnings?.length) console.warn('Cart warnings', warnings);
}

export async function updateLine(lineId: string, quantity: number) {
  console.log('Updating line', lineId, 'to quantity', quantity);
  const cartId = await ensureCartId();
  const { data } = await shClient.request<CartLinesUpdateMutation>(CART_LINES_UPDATE, {
    variables: { cartId, lines: [{ id: lineId, quantity }] } as CartLinesUpdateMutationVariables
  });
  // is everthing ok?
  if (!data || !data.cartLinesUpdate) {
    console.error('Cart lines update failed', data);
    throw new Error('Cart lines update failed');
  }
  cartContainer.cart = data!.cartLinesUpdate!.cart!;
}

export async function removeLines(lineIds: string[]) {
  const cartId = await ensureCartId();
  const { data } = await shClient.request<CartLinesRemoveMutation>(CART_LINES_REMOVE, {
    variables: { cartId, lineIds } as CartLinesRemoveMutationVariables
  });
  cartContainer.cart = data!.cartLinesRemove!.cart!;
}

export async function attachBuyerIdentity(customerAccessToken: string, email?: string) {
  const cartId = await ensureCartId();
  const { data } = await shClient.request<CartBuyerIdentityUpdateMutation>(CART_BUYER_IDENTITY_UPDATE, {
    variables: { cartId, buyerIdentity: { customerAccessToken, email } } as CartBuyerIdentityUpdateMutationVariables
  });
  cartContainer.cart = data!.cartBuyerIdentityUpdate!.cart!;
}
